// This file is auto-generated by @hey-api/openapi-ts

import { queryOptions, type UseMutationOptions } from '@tanstack/react-query';

import { client } from '../client.gen';
import {
	cancelPurchase,
	createPurchase,
	findMostRelevantTicket,
	getActiveOperatorTickets,
	getOperator,
	getOperatorTicketDetail,
	getTariff,
	getTicket,
	getTicketDetail,
	listMyTickets,
	listOperators,
	listTariffs,
	listTickets,
	listZones,
	type Options,
	searchStops,
	sendInvoices,
} from '../sdk.gen';
import type {
	CancelPurchaseData,
	CancelPurchaseError,
	CancelPurchaseResponse,
	CreatePurchaseData,
	CreatePurchaseError,
	CreatePurchaseResponse,
	FindMostRelevantTicketData,
	GetActiveOperatorTicketsData,
	GetOperatorData,
	GetOperatorTicketDetailData,
	GetTariffData,
	GetTicketData,
	GetTicketDetailData,
	ListMyTicketsData,
	ListOperatorsData,
	ListTariffsData,
	ListTicketsData,
	ListZonesData,
	SearchStopsData,
	SendInvoicesData,
	SendInvoicesError,
	SendInvoicesResponse,
} from '../types.gen';

export const createPurchaseMutation = (
	options?: Partial<Options<CreatePurchaseData>>
): UseMutationOptions<
	CreatePurchaseResponse,
	CreatePurchaseError,
	Options<CreatePurchaseData>
> => {
	const mutationOptions: UseMutationOptions<
		CreatePurchaseResponse,
		CreatePurchaseError,
		Options<CreatePurchaseData>
	> = {
		mutationFn: async (fnOptions) => {
			const { data } = await createPurchase({
				...options,
				...fnOptions,
				throwOnError: true,
			});
			return data;
		},
	};
	return mutationOptions;
};

export const cancelPurchaseMutation = (
	options?: Partial<Options<CancelPurchaseData>>
): UseMutationOptions<
	CancelPurchaseResponse,
	CancelPurchaseError,
	Options<CancelPurchaseData>
> => {
	const mutationOptions: UseMutationOptions<
		CancelPurchaseResponse,
		CancelPurchaseError,
		Options<CancelPurchaseData>
	> = {
		mutationFn: async (fnOptions) => {
			const { data } = await cancelPurchase({
				...options,
				...fnOptions,
				throwOnError: true,
			});
			return data;
		},
	};
	return mutationOptions;
};

/**
 * E-mail all paid invoices of a purchase to the passenger, one PDF per invoice
 */
export const sendInvoicesMutation = (
	options?: Partial<Options<SendInvoicesData>>
): UseMutationOptions<
	SendInvoicesResponse,
	SendInvoicesError,
	Options<SendInvoicesData>
> => {
	const mutationOptions: UseMutationOptions<
		SendInvoicesResponse,
		SendInvoicesError,
		Options<SendInvoicesData>
	> = {
		mutationFn: async (fnOptions) => {
			const { data } = await sendInvoices({
				...options,
				...fnOptions,
				throwOnError: true,
			});
			return data;
		},
	};
	return mutationOptions;
};

export type QueryKey<TOptions extends Options> = [
	Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
		_id: string;
		_infinite?: boolean;
		tags?: ReadonlyArray<string>;
	}
];

const createQueryKey = <TOptions extends Options>(
	id: string,
	options?: TOptions,
	infinite?: boolean,
	tags?: ReadonlyArray<string>
): [QueryKey<TOptions>[0]] => {
	const params: QueryKey<TOptions>[0] = {
		_id: id,
		baseUrl:
			options?.baseUrl || (options?.client ?? client).getConfig().baseUrl,
	} as QueryKey<TOptions>[0];
	if (infinite) {
		params._infinite = infinite;
	}
	if (tags) {
		params.tags = tags;
	}
	if (options?.body) {
		params.body = options.body;
	}
	if (options?.headers) {
		params.headers = options.headers;
	}
	if (options?.path) {
		params.path = options.path;
	}
	if (options?.query) {
		params.query = options.query;
	}
	return [params];
};

export const listTicketsQueryKey = (options?: Options<ListTicketsData>) =>
	createQueryKey('listTickets', options);

export const listTicketsOptions = (options?: Options<ListTicketsData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await listTickets({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: listTicketsQueryKey(options),
	});
};

export const getTicketQueryKey = (options: Options<GetTicketData>) =>
	createQueryKey('getTicket', options);

export const getTicketOptions = (options: Options<GetTicketData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getTicket({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getTicketQueryKey(options),
	});
};

export const listTariffsQueryKey = (options?: Options<ListTariffsData>) =>
	createQueryKey('listTariffs', options);

export const listTariffsOptions = (options?: Options<ListTariffsData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await listTariffs({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: listTariffsQueryKey(options),
	});
};

export const getTariffQueryKey = (options: Options<GetTariffData>) =>
	createQueryKey('getTariff', options);

export const getTariffOptions = (options: Options<GetTariffData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getTariff({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getTariffQueryKey(options),
	});
};

export const listZonesQueryKey = (options: Options<ListZonesData>) =>
	createQueryKey('listZones', options);

export const listZonesOptions = (options: Options<ListZonesData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await listZones({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: listZonesQueryKey(options),
	});
};

export const listOperatorsQueryKey = (options?: Options<ListOperatorsData>) =>
	createQueryKey('listOperators', options);

export const listOperatorsOptions = (options?: Options<ListOperatorsData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await listOperators({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: listOperatorsQueryKey(options),
	});
};

export const getOperatorQueryKey = (options: Options<GetOperatorData>) =>
	createQueryKey('getOperator', options);

export const getOperatorOptions = (options: Options<GetOperatorData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getOperator({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getOperatorQueryKey(options),
	});
};

export const getActiveOperatorTicketsQueryKey = (
	options: Options<GetActiveOperatorTicketsData>
) => createQueryKey('getActiveOperatorTickets', options);

export const getActiveOperatorTicketsOptions = (
	options: Options<GetActiveOperatorTicketsData>
) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getActiveOperatorTickets({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getActiveOperatorTicketsQueryKey(options),
	});
};

export const getOperatorTicketDetailQueryKey = (
	options: Options<GetOperatorTicketDetailData>
) => createQueryKey('getOperatorTicketDetail', options);

export const getOperatorTicketDetailOptions = (
	options: Options<GetOperatorTicketDetailData>
) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getOperatorTicketDetail({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getOperatorTicketDetailQueryKey(options),
	});
};

export const searchStopsQueryKey = (options: Options<SearchStopsData>) =>
	createQueryKey('searchStops', options);

export const searchStopsOptions = (options: Options<SearchStopsData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await searchStops({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: searchStopsQueryKey(options),
	});
};

export const listMyTicketsQueryKey = (options?: Options<ListMyTicketsData>) =>
	createQueryKey('listMyTickets', options);

export const listMyTicketsOptions = (options?: Options<ListMyTicketsData>) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await listMyTickets({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: listMyTicketsQueryKey(options),
	});
};

export const getTicketDetailQueryKey = (
	options: Options<GetTicketDetailData>
) => createQueryKey('getTicketDetail', options);

export const getTicketDetailOptions = (
	options: Options<GetTicketDetailData>
) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await getTicketDetail({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: getTicketDetailQueryKey(options),
	});
};

export const findMostRelevantTicketQueryKey = (
	options?: Options<FindMostRelevantTicketData>
) => createQueryKey('findMostRelevantTicket', options);

export const findMostRelevantTicketOptions = (
	options?: Options<FindMostRelevantTicketData>
) => {
	return queryOptions({
		queryFn: async ({ queryKey, signal }) => {
			const { data } = await findMostRelevantTicket({
				...options,
				...queryKey[0],
				signal,
				throwOnError: true,
			});
			return data;
		},
		queryKey: findMostRelevantTicketQueryKey(options),
	});
};
